import useLocalStorage from '@hooks/useLocalStorage' // Assuming you have this hook
import type { Theme } from '@typings/index' // Replace with your actual type import
import { useEffect, useMemo, useState } from 'react'

const useThemeSync = () => {
  const [theme, setTheme] = useLocalStorage<Theme>('theme', 'system')
  const [systemTheme, setSystemTheme] = useState<'dark' | 'light'>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
  )

  // Compute `effectiveTheme` using useMemo
  const effectiveTheme = useMemo(() => {
    return theme === 'system' ? systemTheme : theme
  }, [theme, systemTheme])

  // Sync system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleSystemThemeChange = () => {
      const newSystemTheme = mediaQuery.matches ? 'dark' : 'light'
      setSystemTheme(newSystemTheme)
    }

    handleSystemThemeChange() // Set initial system theme
    mediaQuery.addEventListener('change', handleSystemThemeChange)

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange)
    }
  }, [])

  // Apply theme to document based on effective theme
  useEffect(() => {
    document.documentElement.classList.toggle('dark', effectiveTheme === 'dark')
  }, [effectiveTheme])

  return { theme, setTheme, systemTheme, effectiveTheme }
}

export default useThemeSync
